import { FC } from 'react';
import { SpaceKind } from 'shared';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { BreadcrumbTrail } from '../../../../components/breadcrumb/index';
import { VaultAccess } from '../../../../guards/page-access.util';
import { useLocale } from '../../../../hooks/index';
import { useRecipientDataRoom } from '../../hooks/use-recipient-data-room.hook';
import { useVaultAccess } from '../../hooks/use-recipient-view.hook';
import { useSpacesHome } from '../../hooks/use-spaces-home.hook';
import { useWorkspaceBrowser } from '../../hooks/use-workspace-browser.hook';
import { WorkspaceBrowserViewProps } from '../../types/data-room.types';
import {
  BrowserContentState,
  browserContentState,
  browserTrail,
} from '../../utils/space-browser.util';
import { spaceDescription } from '../../utils/space-list-item.util';
import { MobileListPlaceholder } from '../components/workspaces-loading-placeholders';
import { MobileFolderPanel } from './mobile/mobile-folder-panel';
import { MobileNodeList } from './mobile/mobile-node-list';
import { MobileSearchField } from './mobile/mobile-search-field';
import { MobileSpaceHeader } from './mobile/mobile-space-header';
import { WorkspaceBrowserEmpty } from './workspace-browser-empty';
import { WorkspaceBrowserError } from './workspace-browser-error';
import { WorkspaceBrowserNoMatches } from './workspace-browser-no-matches';

const MOBILE_TRAIL_MAX_VISIBLE = 3;

export const WorkspaceBrowserMobile: FC<WorkspaceBrowserViewProps> = ({
  isRecipientView,
  selection,
}) => {
  const { breadcrumbs, space } = selection;
  const { messagesLocale } = useLocale();
  const { contents, navigation, rowActions, search } = useWorkspaceBrowser({
    isRecipientView,
    selection,
  });
  const { files, folders, isError, isPending, isSearching, pagination, retry } = contents;
  // A recipient has no Home of their own: a room shared with them reads its description itself.
  // A Stakeholder has the Vault's description to show, which the Home of a recipient does not carry.
  const vaultAccess = useVaultAccess();
  const { home } = useSpacesHome({
    enabled: !isRecipientView || vaultAccess === VaultAccess.OWN_FOLDERS,
  });
  const { dataRoom: sharedRoom } = useRecipientDataRoom(
    isRecipientView && space.kind === SpaceKind.DATA_ROOM ? space.id : undefined,
  );
  const homeSpace =
    home && [home.vault, ...home.workspaces, ...home.dataRooms].find(({ id }) => id === space.id);

  const isEmpty = folders.length === 0 && files.length === 0;
  const contentState = browserContentState({ isEmpty, isError, isPending });
  const showsTrail = breadcrumbs.length > 0 && !isSearching;
  const folderName = breadcrumbs[breadcrumbs.length - 1]?.name;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
      <MobileSpaceHeader
        description={homeSpace ? spaceDescription(homeSpace) : (sharedRoom?.description ?? '')}
        name={space.name}
      />
      <div className="flex flex-col">
        <MobileSearchField key={search.searchKey} onSearch={search.onSearch} />
        {showsTrail && (
          <BreadcrumbTrail
            className="py-4"
            ellipsisLabel={messagesLocale['SHOW_HIDDEN_FOLDERS']}
            items={browserTrail({
              breadcrumbs,
              rootLabel: messagesLocale['HOME'],
              spaceId: space.id,
            })}
            label={messagesLocale['BREADCRUMB']}
            maxVisible={MOBILE_TRAIL_MAX_VISIBLE}
            onSelect={navigation.openNode}
          />
        )}
        <MobileFolderPanel
          // The trail's own padding spaces it from the search; without one, the margin does.
          className={showsTrail ? undefined : 'mt-4'}
          count={
            contentState === BrowserContentState.READY
              ? folders.length + files.length + pagination.remainingCount
              : undefined
          }
          onBack={navigation.goBack}
          title={
            isSearching ? (
              <AppFormattedMessage id="SEARCH_RESULTS" />
            ) : (
              (folderName ?? <AppFormattedMessage id="HOME" />)
            )
          }
        >
          {contentState === BrowserContentState.LOADING && <MobileListPlaceholder />}
          {contentState === BrowserContentState.ERROR && <WorkspaceBrowserError onRetry={retry} />}
          {contentState === BrowserContentState.EMPTY &&
            (isSearching ? (
              <WorkspaceBrowserNoMatches isWorkspace={space.kind === SpaceKind.WORKSPACE} />
            ) : (
              <WorkspaceBrowserEmpty />
            ))}
          {contentState === BrowserContentState.READY && (
            <MobileNodeList
              actions={rowActions}
              files={files}
              folders={folders}
              onOpenFolder={navigation.openNode}
              pagination={pagination}
            />
          )}
        </MobileFolderPanel>
      </div>
    </div>
  );
};
